<template>
	<div class="course-player">
		<!-- Sidebar -->
     
		<div class="course-player__sidebar">
			<div class="course-player__sidebar-header">
				<button class="course-player__back-btn" @click="goBack">
					← Назад к курсам
				</button>
				<h2 class="course-player__course-title">{{ course.title }}</h2>
				<div class="course-player__progress">
					<div class="course-player__progress-bar">
						<div
							class="course-player__progress-fill"
							:style="{ width: courseProgress + '%' }"
						></div>
					</div>
					<span class="course-player__progress-text">{{ courseProgress }}%</span>
				</div>
			</div>

			<div class="course-player__modules">
				<div
					v-for="module in modules"
					:key="module.id"
					class="course-player__module"
				>
					<div
						class="course-player__module-header"
						@click="toggleModule(module.id)"
					>
						<span class="course-player__module-icon">
							{{ expandedModules.includes(module.id) ? '▼' : '▶' }}
						</span>
						<h3 class="course-player__module-title">{{ module.title }}</h3>
					</div>

					<div
						v-if="expandedModules.includes(module.id)"
						class="course-player__lessons"
					>
						<div
							v-for="lesson in module.lessons"
							:key="lesson.id"
							class="course-player__lesson"
							:class="{
								'course-player__lesson--active': currentLesson?.id === lesson.id,
								'course-player__lesson--completed': lesson.is_completed,
							}"
							@click="selectLesson(lesson)"
						>
							<span class="course-player__lesson-icon">
								{{ lesson.is_completed ? '✓' : lesson.lesson_type === 'video' ? '🎥' : '📄' }}
							</span>
							<span class="course-player__lesson-title">{{ lesson.title }}</span>
							<span v-if="lesson.duration_minutes" class="course-player__lesson-duration">
								{{ lesson.duration_minutes }} мин
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Main Content -->
		<div class="course-player__main">
			<div v-if="!currentLesson" class="course-player__empty">
				<p>Выберите урок из списка слева</p>
			</div>

				<div v-else class="course-player__content">
					<!-- Video Lesson -->
					<div v-if="currentLesson.lesson_type === 'video'" class="course-player__video">
						<video
							ref="videoPlayer"
							class="course-player__video-element"
							:src="currentLesson.video_url"
							controls
							@play="handleVideoPlay"
							@pause="handleVideoPause"
							@ended="handleVideoEnded"
						></video>
					</div>

				<!-- Article Lesson -->
				<div v-else class="course-player__article">
					<div class="course-player__article-content" v-html="currentLesson.content"></div>
					<button
						v-if="!currentLesson.is_completed"
						class="course-player__complete-btn"
						@click="markLessonCompleted"
					>
						Отметить как пройденный
					</button>
				</div>

				<!-- Lesson Info -->
				<div class="course-player__lesson-info">
					<h1 class="course-player__lesson-title">{{ currentLesson.title }}</h1>
					<p v-if="currentLesson.description" class="course-player__lesson-description">
						{{ currentLesson.description }}
					</p>

					<!-- Materials -->
					<div v-if="currentLesson.materials && currentLesson.materials.length > 0" class="course-player__materials">
						<h3 class="course-player__materials-title">Материалы урока</h3>
						<div class="course-player__materials-list">
							<a
								v-for="(material, index) in currentLesson.materials"
								:key="index"
								:href="material.url"
								target="_blank"
								class="course-player__material-item"
							>
								📎 {{ material.name }}
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { coursesService, modulesService, lessonsService, progressService } from '@/services'

export default {
	name: 'CoursePlayer',
	data() {
		return {
			course: {},
			modules: [],
			currentLesson: null,
			expandedModules: [],
			courseProgress: 0,
			watchTimerId: null,
			lastWatchAt: null,
		}
	},
	mounted() {
		this.loadCourse()
	},
	beforeUnmount() {
		this.stopWatchTimer({ flush: true })
	},
	methods: {
		async loadCourse() {
			try {
				const courseId = this.$route.params.id
				this.course = await coursesService.getById(courseId)
				this.modules = await modulesService.getByCourseId(courseId)

				// Load lessons for each module
				for (const module of this.modules) {
					module.lessons = await lessonsService.getByModuleId(module.id)
				}

				// Expand first module
				if (this.modules.length > 0) {
					this.expandedModules.push(this.modules[0].id)
				}

				// Load progress
				const progress = await progressService.getCourseProgress(courseId)
				this.courseProgress = progress.percentage || 0
			} catch (error) {
				console.error('Ошибка загрузки курса:', error)
				alert('Не удалось загрузить курс')
			}
		},

		toggleModule(moduleId) {
			const index = this.expandedModules.indexOf(moduleId)
			if (index > -1) {
				this.expandedModules.splice(index, 1)
			} else {
				this.expandedModules.push(moduleId)
			}
		},

		selectLesson(lesson) {
			if (this.currentLesson?.lesson_type === 'video') {
				this.stopWatchTimer({ flush: true })
			}
			this.currentLesson = lesson
			this.lastWatchAt = null
		},

		handleVideoPlay() {
			this.startWatchTimer()
		},

		handleVideoPause() {
			this.stopWatchTimer({ flush: true })
		},

		async handleVideoEnded() {
			this.stopWatchTimer({ flush: true })
			await this.markLessonCompleted()
		},

		startWatchTimer() {
			if (this.watchTimerId) return
			this.lastWatchAt = Date.now()
			this.watchTimerId = setInterval(() => {
				this.flushWatchTime(false)
			}, 15000)
		},

		stopWatchTimer({ flush = true } = {}) {
			if (this.watchTimerId) {
				clearInterval(this.watchTimerId)
				this.watchTimerId = null
			}
			if (flush) {
				this.flushWatchTime(true)
			}
			this.lastWatchAt = null
		},

		async flushWatchTime(force) {
			if (!this.currentLesson || this.currentLesson.lesson_type !== 'video') {
				return
			}

			const video = this.$refs.videoPlayer
			if (!video) return

			const now = Date.now()
			const last = this.lastWatchAt ?? now
			const deltaSeconds = Math.floor((now - last) / 1000)
			this.lastWatchAt = now

			const isPlaying = !video.paused && !video.ended
			if (!force && !isPlaying) return

			const lastPositionSeconds = Math.floor(video.currentTime || 0)
			if (deltaSeconds <= 0 && lastPositionSeconds <= 0) return

			try {
				await progressService.updateLessonProgress(this.currentLesson.id, {
					secondsSpent: Math.max(0, deltaSeconds),
					lastPositionSeconds,
				})
			} catch (error) {
				console.warn('Failed to update lesson progress:', error)
			}
		},

		async markLessonCompleted() {
			try {
				await lessonsService.markCompleted(this.currentLesson.id)
				this.currentLesson.is_completed = true
				// Reload progress
				const progress = await progressService.getCourseProgress(this.course.id)
				this.courseProgress = progress.percentage || 0
			} catch (error) {
				console.error('Ошибка отметки урока:', error)
			}
		},

		goBack() {
			this.stopWatchTimer({ flush: true })
			this.$router.push('/student-dashboard')
		},
	},
}
</script>

<style scoped>
.course-player {
	display: flex;
	height: 100vh;
	overflow: hidden;
}

.course-player__sidebar {
	width: 350px;
	background-color: #ffffff;
	border-right: 2px solid #e8e8e8;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
}

.course-player__sidebar-header {
	padding: 24px;
	border-bottom: 2px solid #e8e8e8;
}

.course-player__back-btn {
	background-color: transparent;
	border: none;
	color: #0095ec;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	margin-bottom: 16px;
	padding: 0;
}

.course-player__back-btn:hover {
	text-decoration: underline;
}

.course-player__course-title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 600;
	margin: 0 0 16px 0;
}

.course-player__progress {
	display: flex;
	align-items: center;
	gap: 12px;
}

.course-player__progress-bar {
	flex: 1;
	height: 8px;
	background-color: #e8e8e8;
	border-radius: 4px;
	overflow: hidden;
}

.course-player__progress-fill {
	height: 100%;
	background-color: #0095ec;
	transition: width 0.3s ease;
}

.course-player__progress-text {
	color: #0095ec;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 600;
}

.course-player__modules {
	flex: 1;
	overflow-y: auto;
}

.course-player__module {
	border-bottom: 1px solid #e8e8e8;
}

.course-player__module-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px 24px;
	cursor: pointer;
	transition: background-color 0.2s ease;
}

.course-player__module-header:hover {
	background-color: #f8f8f8;
}

.course-player__module-icon {
	color: #636363;
	font-size: 12px;
}

.course-player__module-title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	font-weight: 600;
	margin: 0;
	flex: 1;
}

.course-player__lessons {
	background-color: #f8f8f8;
}

.course-player__lesson {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 24px 12px 48px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.course-player__lesson:hover {
	background-color: #f0f8ff;
}

.course-player__lesson--active {
	background-color: #e6f4ff;
	border-left: 4px solid #0095ec;
}

.course-player__lesson--completed .course-player__lesson-icon {
	color: #28a745;
}

.course-player__lesson-icon {
	font-size: 16px;
}

.course-player__lesson-title {
	flex: 1;
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
}

.course-player__lesson-duration {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
}

.course-player__main {
	flex: 1;
	overflow-y: auto;
	background-color: #f8f8f8;
}

.course-player__empty {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
}

.course-player__content {
	background-color: #ffffff;
	min-height: 100%;
}

.course-player__video {
	width: 100%;
	background-color: #000000;
}

.course-player__video-element {
	width: 100%;
	max-height: 70vh;
}

.course-player__article {
	padding: 48px;
	max-width: 900px;
	margin: 0 auto;
}

.course-player__article-content {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	line-height: 1.8;
	margin-bottom: 32px;
}

.course-player__complete-btn {
	width: 100%;
	padding: 14px;
	background-color: #28a745;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.course-player__complete-btn:hover {
	background-color: #218838;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.course-player__lesson-info {
	padding: 48px;
	max-width: 900px;
	margin: 0 auto;
}

.course-player__lesson-title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0 0 16px 0;
}

.course-player__lesson-description {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	line-height: 1.6;
	margin: 0 0 32px 0;
}

.course-player__materials {
	padding: 24px;
	background-color: #f8f8f8;
	border-radius: 12px;
}

.course-player__materials-title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 16px 0;
}

.course-player__materials-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.course-player__material-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	background-color: #ffffff;
	border-radius: 8px;
	color: #0095ec;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	text-decoration: none;
	transition: all 0.2s ease;
}

.course-player__material-item:hover {
	background-color: #f0f8ff;
	transform: translateX(4px);
}
</style>
